// Everything around a bug report that is not the game state itself.
//
// Matt, 2026-10-10: "We should generally look across all the apps and make
// sure that we are always passing in any information that might or even
// remotely could be relevant to any bug report." The report already carried
// the full game state, the transcript and the device; what it could not
// answer was "where was the player, who were they at the table, was the
// room connected, did anything throw first, and which build were they on".
// This is that.
//
// Shared module: byte-identical in for-sale, incan-gold, no-thanks,
// space-base, telestrations and thunder. It reads the store by the field
// names those apps already use (state.game / state.gameState / the `game`
// getter; roomMeta / remoteGameMeta; presence / remotePresence ...) and
// simply skips whatever an app doesn't have - so app-specific behaviour
// belongs in which getters exist, not in a fork of this file.
//
// Two rules hold everywhere below:
// - EVERY read is wrapped. A snapshot that throws must never be the reason a
//   report fails to send; a missing field is a smaller loss than a lost report.
// - Compact and private. No tokens, no emails, no drawings or decks - those
//   that matter are in gameState already. Other players appear by uid and
//   display name only, which the game state already shows anyway.

const MAX_ERRORS = 8;
const recentErrors = [];
let captureInstalled = false;

const clip = (value, max = 400) => {
  const text = typeof value === 'string' ? value : String(value ?? '');
  return text.length > max ? `${text.slice(0, max)}…` : text;
};

const describe = (value) => {
  if (value instanceof Error) {
    // Message plus the top of the stack: where it threw is the useful part,
    // the rest of the trace is framework plumbing.
    const stack = (value.stack || '').split('\n').slice(0, 4).join('\n');
    return clip(stack.includes(value.message) ? stack : `${value.message}\n${stack}`, 600);
  }
  if (value && typeof value === 'object') {
    try { return clip(JSON.stringify(value)); } catch { return clip(value); }
  }
  return clip(value);
};

/** Remember an error for the next bug report. Never throws. */
export function noteError(kind, ...parts) {
  try {
    recentErrors.push({
      at: new Date().toISOString(),
      kind,
      message: clip(parts.map(describe).join(' '), 800),
    });
    while (recentErrors.length > MAX_ERRORS) recentErrors.shift();
  } catch {
    // Recording an error must not become an error.
  }
}

/**
 * Listens for uncaught errors, unhandled rejections and console.error, so a
 * report filed right after something broke says what broke - including the
 * caught failures (a refused write, a sync that gave up) that only ever
 * reach the console. Idempotent; installed when bugReports.js is imported.
 */
export function installErrorCapture() {
  if (captureInstalled || typeof window === 'undefined') return;
  captureInstalled = true;
  try {
    window.addEventListener('error', (event) => {
      noteError('error', event?.error || event?.message || 'unknown error');
    });
    window.addEventListener('unhandledrejection', (event) => {
      noteError('rejection', event?.reason ?? 'unknown rejection');
    });
    const original = console.error;
    console.error = (...args) => {
      noteError('console', ...args);
      original.apply(console, args);
    };
  } catch {
    // Without capture, reports still send - they just carry no error trail.
  }
}

export const recentErrorsForReport = () => recentErrors.map((e) => ({ ...e }));

// Reads one value; a throw or an undefined leaves the key out entirely.
const put = (target, key, read) => {
  try {
    const value = read();
    if (value !== undefined) target[key] = value;
  } catch {
    // See the file comment: skip, never fail.
  }
};

const isScalar = (v) => v === null || ['string', 'number', 'boolean'].includes(typeof v);

const scalarsOf = (obj, skip = []) => {
  const out = {};
  if (!obj || typeof obj !== 'object') return out;
  Object.keys(obj).forEach((key) => {
    if (!skip.includes(key) && isScalar(obj[key])) out[key] = typeof obj[key] === 'string' ? clip(obj[key], 120) : obj[key];
  });
  return out;
};

const playerLine = (p, index) => {
  const out = { seat: p.seat ?? p.seatIndex ?? index };
  put(out, 'id', () => p.id ?? p.uid);
  put(out, 'name', () => (p.name ?? p.displayName) || undefined);
  if (p.isBot || p.isAI) out.bot = true;
  if (p.difficulty) out.difficulty = p.difficulty;
  return out;
};

/** The browser and the build, as the reporter's tab sees them right now. */
export function clientSnapshot() {
  const c = {};
  put(c, 'version', () => process.env.VUE_APP_GIT_SHA || 'unknown');
  put(c, 'buildTime', () => process.env.VUE_APP_BUILD_TIME || null);
  put(c, 'online', () => navigator.onLine);
  put(c, 'visibility', () => document.visibilityState);
  put(c, 'standalone', () => Boolean(window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator.standalone === true));
  put(c, 'serviceWorker', () => (navigator.serviceWorker?.controller ? 'controlled' : 'none'));
  put(c, 'viewport', () => `${window.innerWidth}x${window.innerHeight}@${window.devicePixelRatio || 1}x`);
  put(c, 'orientation', () => window.screen?.orientation?.type || null);
  put(c, 'language', () => navigator.language);
  put(c, 'timeZone', () => Intl.DateTimeFormat().resolvedOptions().timeZone);
  // How long this tab has been open: a stale tab running old code across a
  // deploy has explained more than one report.
  put(c, 'pageAgeSec', () => Math.round(performance.now() / 1000));
  // What is on screen, read from the DOM so no app has to wire it: the
  // top-level views under #app, and any dialog that is open over them.
  put(c, 'views', () => [...(document.getElementById('app')?.children || [])]
    .map((el) => el.classList?.[0] || el.tagName.toLowerCase())
    .slice(0, 8));
  put(c, 'openDialogs', () => [...document.querySelectorAll('[role="dialog"], [aria-modal="true"], dialog[open]')]
    .map((el) => el.getAttribute('aria-label') || el.classList?.[0] || el.tagName.toLowerCase())
    .filter((label) => label !== 'Report a bug')
    .slice(0, 8));
  put(c, 'recentErrors', () => recentErrorsForReport());
  return c;
}

// Store getters worth a line in a report whenever the app has them: whose
// turn it is from this seat's point of view, what the UI thinks is pending.
const FLAG_GETTERS = [
  'inPlay', 'isFinished', 'isOnline', 'isHost', 'isApproved', 'isAdmin', 'isSiteAdmin',
  'isLoggedIn', 'myUid', 'driverUid', 'absentPlayerIds',
  // for-sale
  'isBuying', 'isSelling', 'myTurnToBid', 'awaitingMyPick', 'picksRevealed', 'tableHighBid',
  // incan-gold
  'awaitingMyChoice', 'choicesRevealed', 'pathGems', 'cardsLeft',
  // no-thanks
  'isRoundOver', 'isHumanTurn', 'isMyOnlineTurn', 'canDecline',
  // telestrations
  'inLobby', 'mySeat', 'iHaveSubmitted', 'everyoneDone', 'isRevealing', 'pageTurnerUid',
  'iDriveReveal', 'enoughPlayers',
  // thunder / space-base
  'isMyTurn', 'canUndo',
];

/**
 * Everything a bug report should carry besides the game state itself.
 * `store` is the Vuex store; `route` the current vue-router route if the app
 * has one. Never throws.
 */
export function gatherAppState({ store, route } = {}) {
  const out = {};
  put(out, 'client', () => clientSnapshot());

  let s = {};
  let g = {};
  try {
    s = store?.state || {};
    g = store?.getters || {};
  } catch {
    // A store we cannot read still leaves the client snapshot above.
  }

  put(out, 'route', () => (route ? {
    name: route.name ?? null,
    path: route.fullPath ?? route.path ?? null,
    params: scalarsOf(route.params),
  } : undefined));

  put(out, 'auth', () => ({
    authReady: s.authReady ?? null,
    siteUserReady: s.siteUserReady ?? null,
    signedIn: Boolean(s.user),
    uid: s.user?.uid || null,
    displayName: s.user?.displayName || null,
    siteStatus: s.siteUser?.status || null,
  }));

  put(out, 'flags', () => {
    const flags = {};
    FLAG_GETTERS.forEach((name) => {
      if (!(name in g)) return;
      put(flags, name, () => {
        const v = g[name];
        if (Array.isArray(v)) return v.filter(isScalar).slice(0, 12);
        return isScalar(v) ? v : undefined;
      });
    });
    return flags;
  });

  put(out, 'me', () => {
    const me = g.me;
    return me ? playerLine(me, me.seat) : undefined;
  });

  // The room, whichever family of field names this app uses.
  put(out, 'room', () => {
    const id = s.roomId ?? s.remoteGameId ?? null;
    const meta = s.roomMeta ?? s.remoteGameMeta ?? null;
    if (!id && !meta) return null;
    const presence = s.presence ?? s.remotePresence ?? {};
    const error = s.roomError ?? s.remoteError ?? null;
    return {
      id,
      ...scalarsOf(meta),
      isHost: Boolean(meta && s.user && meta.hostUid === s.user.uid),
      seats: (meta?.playerSlots ? Object.values(meta.playerSlots) : []).map(playerLine),
      connected: Object.keys(presence || {}).filter((uid) => presence[uid]),
      error: error ? describe(error) : null,
    };
  });

  // A compact summary of the game state (the whole thing rides along as
  // gameState): its scalar fields, its meta's, and who is at the table.
  put(out, 'game', () => {
    const game = s.game ?? s.gameState ?? g.game ?? null;
    if (!game) return null;
    return {
      ...scalarsOf(game),
      meta: scalarsOf(game.meta, ['rngState', 'seed']),
      players: (Array.isArray(game.players) ? game.players : []).map(playerLine),
      logLength: Array.isArray(game.log) ? game.log.length
        : (Array.isArray(game.meta?.log) ? game.meta.log.length : null),
    };
  });

  put(out, 'local', () => {
    const local = {};
    put(local, 'settings', () => (s.settings ? scalarsOf(s.settings) : undefined));
    put(local, 'hasSavedGame', () => s.hasSavedGame);
    put(local, 'thinkingPlayerId', () => s.thinkingPlayerId);
    put(local, 'tutorialActive', () => s.tutorialActive);
    put(local, 'undoDepth', () => (Array.isArray(s.undoStack) ? s.undoStack.length : undefined));
    put(local, 'hasUndoState', () => ('undoState' in s ? Boolean(s.undoState) : undefined));
    put(local, 'round', () => (s.round && typeof s.round === 'object' ? scalarsOf(s.round) : undefined));
    put(local, 'draft', () => {
      if (!('draft' in s)) return undefined;
      const d = s.draft;
      if (d == null) return null;
      if (typeof d === 'string') return { kind: 'text', length: d.length };
      return { kind: d.kind ?? d.type ?? typeof d, ...scalarsOf(d) };
    });
    return local;
  });

  return out;
}
